
import { supabase } from '@/integrations/supabase/client';

export interface LocationHistoryEntry {
  id: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  speed?: number;
  rental_mileage?: number;
  battery_percentage?: number;
  address?: string;
}

export interface LocationPoint {
  id: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  speed?: number;
  rental_mileage?: number;
  battery_percentage?: number;
  battery_level?: number; // Добавлено для совместимости
  address?: string;
  data_source?: 'tracking_history' | 'current_state' | 'supabase' | 'test'; // Расширяем union тип
  online?: boolean; // Добавлено для совместимости
}

export interface LocationHistoryData {
  entries: LocationHistoryEntry[];
  totalEntries: number;
  dateRange: {
    start: Date;
    end: Date;
  };
}

/**
 * Получает историю местоположений для велосипеда
 */
export const fetchLocationHistory = async (
  startDate: Date,
  endDate: Date,
  bikeId?: string,
  courierId?: string,
  forceRefresh: boolean = false,
  filterByMoscowRegion: boolean = false
): Promise<LocationPoint[]> => {
  try {
    console.log(`Загрузка истории местоположений`, { bikeId, courierId, startDate, endDate });
    
    let query = supabase
      .from('bike_tracking_history')
      .select('*')
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(1000);

    if (bikeId) {
      query = query.eq('bike_id', bikeId);
    }
    
    if (courierId) {
      query = query.eq('courier_id', courierId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Ошибка при загрузке истории местоположений:', error);
      throw error;
    }

    const entries: LocationPoint[] = (data || []).map(entry => ({
      id: entry.id,
      timestamp: entry.created_at,
      latitude: parseFloat(entry.latitude || '0'),
      longitude: parseFloat(entry.longitude || '0'),
      speed: entry.speed ? parseFloat(entry.speed.toString()) : undefined,
      rental_mileage: entry.rental_mileage ? parseFloat(entry.rental_mileage.toString()) : undefined,
      battery_percentage: entry.battery_percentage || undefined,
      battery_level: entry.battery_percentage || undefined, // Дублируем для совместимости
      online: true, // По умолчанию считаем онлайн если есть данные
      data_source: 'supabase' as const
    }));

    return entries;
  } catch (error) {
    console.error('Ошибка при получении истории местоположений:', error);
    throw error;
  }
};

/**
 * Получает базовую информацию о велосипеде
 */
export const fetchBikeInfo = async (bikeId: string) => {
  try {
    const { data, error } = await supabase
      .from('bikes')
      .select('*')
      .eq('id', bikeId)
      .single();

    if (error) {
      console.error('Ошибка при загрузке информации о велосипеде:', error);
      throw error;
    }

    return {
      id: data?.id,
      model: data?.model,
      serial_number: data?.serial_number,
      gps_tag: data?.gps_tag,
      status: data?.status,
      battery_percentage: data?.battery_percentage,
      mileage: data?.mileage,
      online: data?.online
    };
  } catch (error) {
    console.error('Ошибка при получении информации о велосипеде:', error);
    throw error;
  }
};

/**
 * Проверяет доступность исторических данных для велосипеда
 */
export const checkHistoryAvailability = async (bikeId: string): Promise<boolean> => {
  try {
    const { count, error } = await supabase
      .from('bike_tracking_history')
      .select('id', { count: 'exact', head: true })
      .eq('bike_id', bikeId)
      .limit(1);

    if (error) {
      console.error('Ошибка при проверке доступности истории:', error);
      return false;
    }

    return (count || 0) > 0;
  } catch (error) {
    console.error('Ошибка при проверке доступности истории:', error);
    return false;
  }
};

/**
 * Форматирует адрес для отображения
 */
export const formatAddress = (address?: string): string => {
  if (!address) return 'Адрес не определен';
  return address.length > 50 ? `${address.substring(0, 47)}...` : address;
};

/**
 * Вычисляет расстояние между двумя точками в километрах
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Радиус Земли в километрах
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};
